import { weekFileName } from '../../lib/weekFileName';
import { DocLangToggle } from '../ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Star, Download, FileText, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ScoreBadge = ({ value, max = 2 }) => {
  const { t } = useTranslation();
  const ratio = max > 0 ? value / max : 0;
  const colors = ratio >= 0.75 ? 'bg-green-100 text-green-700' : ratio >= 0.375 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700';
  return <Badge className={`${colors} font-mono`} data-testid="score-badge">{typeof value === 'number' ? value.toFixed(2) : value}/{max}</Badge>;
};

const ScoreSelector = ({ value, onChange, label }) => (
  <div className="space-y-1">
    <Label className="text-xs text-gray-500">{label}</Label>
    <div className="flex gap-1">
      {[0, 1, 2].map(score => (
        <button
          key={score}
          type="button"
          onClick={() => onChange(score)}
          className={`w-10 h-10 rounded-lg border-2 text-sm font-bold transition-all ${
            value === score
              ? score === 2 ? 'border-green-500 bg-green-50 text-green-700'
                : score === 1 ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                : 'border-red-500 bg-red-50 text-red-700'
              : 'border-gray-200 hover:border-gray-400 text-gray-400'
          }`}
          data-testid={`score-btn-${label.toLowerCase().replace(/\s/g, '-')}-${score}`}
        >
          {score}
        </button>
      ))}
    </div>
  </div>
);

const EmployeeRatings = ({ token, employees = [], projects = [], currentUser }) => {
  const { t } = useTranslation();
  const [aggregated, setAggregated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [ratingDialog, setRatingDialog] = useState({ open: false, projectId: null, employeeId: null });
  const [ratingForm, setRatingForm] = useState({ quality_of_service: 0, financial_rating: 0, on_time_delivery: 0, communication: 0, notes: '' });
  const [saving, setSaving] = useState(false);
  const [projectRatings, setProjectRatings] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');

  const isAdmin = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';
  const canRate = isAdmin || isManager;

  const fetchAggregated = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/iso/ratings/aggregated`);
      setAggregated(res.data);
    } catch (err) {
      console.error('Error fetching ratings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjectRatings = useCallback(async (projectId) => {
    try {
      const res = await axios.get(`${API}/iso/ratings?project_id=${projectId}`);
      setProjectRatings(res.data);
    } catch (err) {
      console.error('Error fetching project ratings:', err);
    }
  }, []);

  useEffect(() => { fetchAggregated(); }, [fetchAggregated]);

  useEffect(() => {
    if (selectedProject) fetchProjectRatings(selectedProject);
  }, [selectedProject, fetchProjectRatings]);

  const openRatingDialog = (projectId, employeeId) => {
    const existing = projectRatings.find(r => r.employee_id === employeeId && r.project_id === projectId);
    setRatingForm({
      quality_of_service: existing?.quality_of_service || 0,
      financial_rating: existing?.financial_rating || 0,
      on_time_delivery: existing?.on_time_delivery || 0,
      communication: existing?.communication || 0,
      notes: existing?.notes || ''
    });
    setRatingDialog({ open: true, projectId, employeeId });
  };

  const saveRating = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/iso/ratings`, {
        project_id: ratingDialog.projectId,
        employee_id: ratingDialog.employeeId,
        ...ratingForm
      });
      toast.success(t('auto.iso.ratingSaved'));
      setRatingDialog({ open: false, projectId: null, employeeId: null });
      fetchAggregated();
      if (selectedProject) fetchProjectRatings(selectedProject);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rating');
    } finally {
      setSaving(false);
    }
  };

  const exportPdf = async () => {
    try {
      toast.info(t('auto.iso.generatingPdf'));
      const res = await fetch(`${API}/iso/ratings/export/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to export');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = weekFileName(`Employee_Ratings_${new Date().toISOString().slice(0,10)}.pdf`);
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);
      toast.success(t('auto.iso.pdfExported'));
    } catch (err) { toast.error(err.message); }
  };

  const exportExcel = async () => {
    try {
      toast.info(t('auto.iso.generatingExcel'));
      const res = await fetch(`${API}/iso/ratings/export/excel`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to export');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = weekFileName(`Employee_Ratings_${new Date().toISOString().slice(0,10)}.xlsx`);
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);
      toast.success(t('auto.iso.excelExported'));
    } catch (err) { toast.error(err.message); }
  };

  const empMap = {};
  employees.forEach(e => { empMap[e.id] = e; });

  const selectedProjectData = projects.find(p => p.id === selectedProject);
  const teamMembers = selectedProjectData?.team_members || [];

  if (loading) return <div className="flex items-center justify-center p-8 text-gray-500">{t('auto.iso.loadingRatings')}</div>;

  return (
    <div className="space-y-6" data-testid="employee-ratings-tab">
      {/* Aggregated Ratings Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-500" />
                {t('auto.iso.employeeRatingsOverview')}
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">{t('auto.iso.aggregatedScoresAcrossAllProjectsOverall')}</p>
            </div>
            <div className="flex gap-2">
              <DocLangToggle testId="doc-lang-employeeratings-0" />
              <Button variant="outline" size="sm" onClick={exportPdf} data-testid="export-ratings-pdf">
                <FileText className="h-4 w-4 mr-1" /> {t('auto.iso.pdf')}
              </Button>
              <Button variant="outline" size="sm" onClick={exportExcel} data-testid="export-ratings-excel">
                <Download className="h-4 w-4 mr-1" /> {t('auto.iso.excel')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {aggregated.length === 0 ? (
            <p className="text-center text-gray-500 py-8">{t('auto.iso.noRatingsYetRateEmployeesFrom')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="aggregated-ratings-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-medium">#</th>
                    <th className="text-left p-3 font-medium">{t('auto.iso.employee')}</th>
                    <th className="text-center p-3 font-medium">{t('auto.iso.projects')}</th>
                    <th className="text-center p-3 font-medium">{t('auto.iso.quality')}</th>
                    <th className="text-center p-3 font-medium">{t('auto.iso.financial')}</th>
                    <th className="text-center p-3 font-medium">{t('auto.iso.onTime')}</th>
                    <th className="text-center p-3 font-medium">{t('auto.iso.communication')}</th>
                    <th className="text-center p-3 font-medium">{t('auto.iso.overall8')}</th>
                  </tr>
                </thead>
                <tbody>
                  {aggregated.map((row, idx) => (
                    <React.Fragment key={row.employee_id}>
                      <tr
                        className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => setExpandedEmployee(expandedEmployee === row.employee_id ? null : row.employee_id)}
                        data-testid={`rating-row-${row.employee_id}`}
                      >
                        <td className="p-3 text-gray-400">{idx + 1}</td>
                        <td className="p-3">
                          <div className="font-medium">{row.employee_name}</div>
                          <div className="text-xs text-gray-500">{row.position}</div>
                        </td>
                        <td className="p-3 text-center">{row.project_count}</td>
                        <td className="p-3 text-center"><ScoreBadge value={row.avg_quality_of_service} /></td>
                        <td className="p-3 text-center"><ScoreBadge value={row.avg_financial_rating} /></td>
                        <td className="p-3 text-center"><ScoreBadge value={row.avg_on_time_delivery} /></td>
                        <td className="p-3 text-center"><ScoreBadge value={row.avg_communication} /></td>
                        <td className="p-3 text-center"><ScoreBadge value={row.overall_score} max={8} /></td>
                      </tr>
                      {expandedEmployee === row.employee_id && (
                        <tr>
                          <td colSpan={8} className="bg-blue-50/50 p-4">
                            <div className="text-xs font-medium text-gray-600 mb-2">{t('auto.iso.perProjectBreakdown')}</div>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left p-1">{t('auto.iso.project')}</th>
                                  <th className="text-center p-1">{t('auto.iso.quality')}</th>
                                  <th className="text-center p-1">{t('auto.iso.financial')}</th>
                                  <th className="text-center p-1">{t('auto.iso.onTime')}</th>
                                  <th className="text-center p-1">{t('auto.iso.comm')}</th>
                                  <th className="text-center p-1">{t('auto.iso.total')}</th>
                                  <th className="text-left p-1">{t('auto.iso.notes')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {row.projects.map((proj, pi) => (
                                  <tr key={pi} className="border-t border-blue-100">
                                    <td className="p-1 font-medium">{proj.project_name}</td>
                                    <td className="p-1 text-center">{proj.quality_of_service}</td>
                                    <td className="p-1 text-center">{proj.financial_rating}</td>
                                    <td className="p-1 text-center">{proj.on_time_delivery}</td>
                                    <td className="p-1 text-center">{proj.communication}</td>
                                    <td className="p-1 text-center font-bold">
                                      {proj.quality_of_service + proj.financial_rating + proj.on_time_delivery + proj.communication}/8
                                    </td>
                                    <td className="p-1 text-gray-500">{proj.notes || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rate by Project */}
      {canRate && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('auto.iso.rateEmployeesByProject')}</CardTitle>
            <p className="text-sm text-gray-500">{t('auto.iso.selectAProjectToRateIts')}</p>
          </CardHeader>
          <CardContent>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-full max-w-md" data-testid="rating-project-select">
                <SelectValue placeholder={t('auto.iso.selectAProject')} />
              </SelectTrigger>
              <SelectContent>
                {projects
                  .filter(p => isAdmin || p.owner_id === currentUser?.employee_id || p.owner_id === currentUser?.id)
                  .map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))
                }
              </SelectContent>
            </Select>

            {selectedProject && teamMembers.length > 0 && (
              <div className="mt-4 space-y-2">
                {teamMembers.map(tm => {
                  const emp = empMap[tm.employee_id];
                  const existing = projectRatings.find(r => r.employee_id === tm.employee_id);
                  const total = existing ? (existing.quality_of_service + existing.financial_rating + existing.on_time_delivery + existing.communication) : null;
                  return (
                    <div key={tm.employee_id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50">
                      <div>
                        <div className="font-medium text-sm">{emp?.name || 'Unknown'}</div>
                        <div className="text-xs text-gray-500">{tm.role || emp?.position || ''}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        {total !== null && (
                          <span className="text-sm text-gray-500">Score: {total}/8</span>
                        )}
                        <Button
                          size="sm"
                          variant={existing ? "outline" : "default"}
                          onClick={() => openRatingDialog(selectedProject, tm.employee_id)}
                          data-testid={`rate-btn-${tm.employee_id}`}
                        >
                          <Star className="h-3 w-3 mr-1" />
                          {existing ? t('auto.iso.update') : t('auto.iso.rate')}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {selectedProject && teamMembers.length === 0 && (
              <p className="mt-4 text-sm text-gray-500">{t('auto.iso.noTeamMembersAssignedToThis')}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Rating Dialog */}
      <Dialog open={ratingDialog.open} onOpenChange={(open) => !open && setRatingDialog({ open: false, projectId: null, employeeId: null })}>
        <DialogContent className="sm:max-w-md" aria-describedby="rating-dialog-desc">
          <DialogHeader>
            <DialogTitle>{t('auto.iso.rateEmployee')}</DialogTitle>
            <DialogDescription id="rating-dialog-desc">
              {empMap[ratingDialog.employeeId]?.name} - {projects.find(p => p.id === ratingDialog.projectId)?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
            <ScoreSelector label={t('auto.iso.qualityOfService')} value={ratingForm.quality_of_service} onChange={v => setRatingForm(prev => ({ ...prev, quality_of_service: v }))} />
            <ScoreSelector label={t('auto.iso.financialRating')} value={ratingForm.financial_rating} onChange={v => setRatingForm(prev => ({ ...prev, financial_rating: v }))} />
            <ScoreSelector label={t('auto.iso.onTimeDelivery')} value={ratingForm.on_time_delivery} onChange={v => setRatingForm(prev => ({ ...prev, on_time_delivery: v }))} />
            <ScoreSelector label={t('auto.iso.communication')} value={ratingForm.communication} onChange={v => setRatingForm(prev => ({ ...prev, communication: v }))} />
          </div>
          <div>
            <Label className="text-xs text-gray-500">{t('auto.iso.notesOptional')}</Label>
            <Textarea
              placeholder={t('auto.iso.additionalComments')}
              value={ratingForm.notes}
              onChange={e => setRatingForm(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
              data-testid="rating-notes-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRatingDialog({ open: false, projectId: null, employeeId: null })} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
            <Button onClick={saveRating} disabled={saving} data-testid="save-rating-btn">
              {saving ? t('auto.iso.saving') : t('auto.iso.saveRating')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeRatings;
